import {
  isFulfilled,
  type Middleware,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit'
import { moderationApi } from './moderationApi'
import { CONTENT_WRITE_ENDPOINTS } from './draftProbeMiddleware'
import { refetchCourseModeration, type ModerationSelectState } from './lib/refetchCourseModeration'

type State = ModerationSelectState
type Dispatch = ThunkDispatch<State, unknown, UnknownAction>

interface WriteMeta {
  arg?: { type?: string; endpointName?: string; originalArgs?: unknown }
}

/** Курс, в который пишет мутация: `courseId`/`course_id` в аргументах, у `updateCourse` — `id`. */
const courseIdOf = (endpoint: string, args: unknown): number | null => {
  if (!args || typeof args !== 'object') return null
  const a = args as Record<string, unknown>
  const id = a.courseId ?? a.course_id ?? (endpoint === 'updateCourse' ? a.id : undefined)
  return typeof id === 'number' ? id : null
}

/**
 * «Обновить» в шапке — сразу после правки живого курса. Без этого студия узнавала
 * о несданных правках только из опроса `/moderation` (у живого курса раз в 30 с),
 * если черновик уже был заведён раньше (зонд первого черновика —
 * `draftProbeMiddleware` — в этом случае не стоит), и кнопка появлялась с опозданием.
 *
 * Флаг ставится оптимистично и СРАЗУ перепроверяется запросом: сквозные поля
 * (цена, режим продажи — `updateCourse`) бэк пишет без черновика, и для них
 * оптимистики нет вовсе, только перепроверка.
 */
export const unsubmittedChangesMiddleware: Middleware<object, State, Dispatch> =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    const result = next(action)
    if (!isFulfilled(action)) return result
    const arg = (action.meta as WriteMeta).arg
    const endpoint = arg?.endpointName ?? ''
    if (arg?.type !== 'mutation' || !CONTENT_WRITE_ENDPOINTS.has(endpoint)) return result
    const courseId = courseIdOf(endpoint, arg.originalArgs)
    if (courseId === null) return result
    const cached = moderationApi.endpoints.getCourseModeration.select(courseId)(getState()).data
    // Только живой курс, где есть что показывать, и не во время проверки.
    if (!cached || cached.visibility === 'unpublished' || cached.review?.status === 'pending')
      return result
    if (endpoint !== 'updateCourse' && !cached.hasUnsubmittedChanges) {
      dispatch(
        moderationApi.util.updateQueryData('getCourseModeration', courseId, (draft) => {
          draft.hasUnsubmittedChanges = true
        }),
      )
    }
    void refetchCourseModeration(dispatch, getState, courseId)
    return result
  }
